// Dutch (je) copy for /web-uploader, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "De web-uploader" for the web uploader; "previews" and "originelen".
// The uploader's own labels are in English, so they're quoted as it shows them (“Previews”, “Originals”).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const nl: WebUploaderCopy = {
  seo: {
    title: "De web-uploader van Photohawk | Sneller dan FTP",
    description:
      "We hebben de web-uploader van Photohawk opnieuw gebouwd. Hij verstuurt veel foto's tegelijk, kan uploads van meer dan 100.000 foto's aan en gaat vanzelf verder als je verbinding wegvalt, bij alle abonnementen. Bij alle abonnementen gaan eerst lichte previews omhoog, zodat je galerij binnen minuten online staat, niet pas na uren.",
  },
  hero: {
    badge: "Nieuw",
    eyebrow: "Bij alle abonnementen",
    title: "De nieuwe web-uploader van Photohawk",
    subtitle:
      "Sneller dan FTP, gebouwd voor uploads van meer dan 100.000 foto's, en hij gaat verder waar hij gebleven was.",
    plans: "Bij alle abonnementen staat een groot evenement dankzij eerst de previews binnen minuten online, niet pas na uren.",
    ctaPrimary: "Open de web-uploader",
    ctaSecondary: "Bekijk wat er nieuw is",
    screenshotAlt:
      "De web-uploader midden in een upload. Met eerst de previews (alle abonnementen) laat de statusbalk zien dat de galerij online staat met previews en hoeveel mensen hem bezoeken, en toont de wachtrij rechts de previews die klaar zijn en de originelen die onderweg zijn, met twee gekochte foto's die voorgaan.",
    phoneAlt: "De web-uploader op een telefoon, geopend in een map van de galerij, met de uploadstatus onderaan.",
  },
  previews: {
    heading: "Eerst de previews",
    badge: "Alle abonnementen",
    subtitle:
      "Bij alle abonnementen: binnen minuten online, niet pas na uren. Een galerij van 10.000 foto's kan met een uploadsnelheid van 20 Mbps in ongeveer 8 minuten online staan en verkopen.",
    body: "Eerst gaan lichte previews omhoog, zodat je galerij al online kan en kan verkopen, of gratis downloads kan aanbieden, terwijl de originelen volgen. Koopt of downloadt iemand een foto voordat het origineel binnen is, dan gaat dat origineel voor in de wachtrij.",
    steps: [
      { id: "previews", text: "Eerst gaan lichte previews omhoog" },
      { id: "live", text: "Je galerij gaat online" },
      { id: "buy", text: "Iemand koopt een foto" },
      { id: "jump", text: "Het origineel gaat voor in de wachtrij" },
      { id: "originals", text: "De originelen volgen" },
    ],
    anim: {
      ...en.previews.anim,
      label: "Geanimeerde illustratie van eerst de previews",
      description:
        "Lichte previews van zes foto's vliegen van je computer naar je galerij, en de galerij springt op “Live”. Iemand tikt bij één foto op “Buy”: het origineel gaat naar de kop van de wachtrij met originelen en gaat als eerste omhoog. De andere originelen volgen.",
    },
    link: "Zo werken uploads met eerst de previews",
  },
  faster: {
    heading: "Waarom hij sneller is dan FTP",
    subtitle: "Dezelfde foto's, op twee manieren verstuurd.",
    anim: {
      ...en.faster.anim,
      label: "Geanimeerde vergelijking van een FTP-programma en de web-uploader",
      description:
        "Twee rijen. Bovenaan verstuurt een FTP-programma foto's standaard 2 tegelijk, en nooit meer dan 10. Onderaan verstuurt de web-uploader veel foto's tegelijk, over banen die breder en smaller worden naargelang de verbinding. Bij alle abonnementen gaan lichte previews eerst, dus de galerij staat eerder online. De verbinding valt even weg en de upload gaat vanzelf verder.",
      legendPreviews: "Previews",
      legendOriginals: "Originelen",
    },
    points: [
      {
        id: "parallel",
        title: "Veel foto's tegelijk",
        body: "Hij verstuurt veel foto's tegelijk, veel meer dan een FTP-programma als FileZilla (standaard 2 tegelijk, en nooit meer dan 10).",
      },
      {
        id: "adaptive",
        title: "Haalt alles uit je verbinding",
        body: "Hij past zich tijdens het uploaden voortdurend aan, om alles uit je verbinding te halen.",
      },
      {
        id: "scale",
        title: "Gebouwd voor grote evenementen",
        body: "We hebben hem op grote schaal getest, en uploads van meer dan 100.000 foto's kan hij moeiteloos aan.",
      },
      {
        id: "live",
        title: "Binnen minuten online, niet pas na uren (alle abonnementen)",
        body: "Bij alle abonnementen gaan eerst lichte previews omhoog, zodat een galerij van 10.000 foto's met een uploadsnelheid van 20 Mbps in ongeveer 8 minuten online kan staan en verkopen, terwijl de originelen volgen.",
      },
      {
        id: "resume",
        title: "Gaat verder waar hij gebleven was",
        body: "Valt je verbinding weg, dan gaat hij vanzelf verder. Voeg de map opnieuw toe en alleen wat ontbreekt wordt verstuurd.",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "Hoe lang duurt je upload?",
    subtitle: "Een schatting voor je galerij, op basis van je eigen uploadsnelheid.",
    heroLink: "Schat je uploadtijd",
    photos: "Aantal foto's",
    custom: "Anders",
    customLabel: "Aantal foto's (anders)",
    size: "Gemiddelde grootte van een origineel",
    sizeUnit: "MB",
    sizeHint: "JPEG's van evenementen zijn vaak ongeveer {mb} MB.",
    speed: "Je uploadsnelheid",
    speedUnit: "Mbps",
    test: "Test mijn uploadsnelheid",
    testing: "Bezig met testen… {mbps} Mbps",
    testStarting: "Bezig met testen…",
    testDone: "Gemeten: {mbps} Mbps upload",
    testFailed: "We konden je snelheid nu niet meten. Vul hem zelf in, of probeer een algemene snelheidstest.",
    dataNote: "Gebruikt tot ongeveer {mb} MB aan data, en alleen als je op de knop tikt.",
    otherTest: "Of gebruik een algemene snelheidstest",
    resultsHeading: "Je schatting",
    previewsLabel: "Eerst de previews",
    previewsPlans: "Alle abonnementen",
    previewsLive: "Galerij online in {time}",
    previewsAll: "Alle originelen geüpload in {time}",
    originalsLabel: "Alleen originelen",
    originalsLive: "Galerij online in {time}",
    originalsWhen: "zodra alle {gb} GB aan originelen erop staat",
    computerNote: "Bij deze snelheid bepaalt je computer het tempo: hoe snel hij de previews maakt.",
    disclaimer: "Alleen schattingen. Echte tijden hangen af van je verbinding, je computer en je foto's.",
    time: {
      under: "minder dan een minuut",
      min: "ongeveer {n} min",
      h: "ongeveer {n} uur",
      d: "ongeveer {n} dagen",
    },
    table: {
      caption: "Voorbeeld: 10.000 foto's",
      speed: "Uploadsnelheid",
      live: "Eerst de previews: galerij online (alle abonnementen)",
      all: "Alle originelen geüpload",
      note: "Het cijfer van ongeveer 8 minuten is gemeten bij een uploadsnelheid van 20 Mbps (eerst de previews). De andere rijen zijn schattingen met hetzelfde model, met originelen van ongeveer {mb} MB.",
    },
  },
  features: {
    heading: "Nog meer nieuw",
    subtitle: "Echte schermen uit de nieuwe web-uploader.",
    items: [
      {
        id: "status",
        title: "De status in gewone woorden",
        body: "Zie in één oogopslag hoe het ervoor staat. Met eerst de previews (alle abonnementen) is dat eerst “Previews”, dan “Originals” (originelen), elk met een eigen voortgang.",
        alt: "De uploadstatus met eerst de previews (alle abonnementen): “Live — saving originals…”, een voortgangsregel voor “Previews” en een voor “Originals”, en “2 photos people bought go first”.",
      },
      {
        id: "queue",
        title: "Een wachtrij die staat waar jij wilt",
        body: "Zet de uploadwachtrij rechts, links of onderaan vast en sleep hem naar het formaat dat je wilt. Je browser onthoudt je keuze.",
        alt: "De uploadwachtrij onderaan in de uploader vastgezet, met het menu open om hem naar rechts, onder of links te verplaatsen.",
      },
      {
        id: "visitors",
        title: "Zie wie er kijkt",
        body: "De statusbalk laat zien hoeveel mensen je galerij op dit moment bekijken, bij alle abonnementen.",
        alt: "De statusbalk bovenaan de uploader: “Live on previews” (met eerst de previews) en het aantal bezoekers.",
      },
      {
        id: "resume",
        title: "Gaat verder waar hij gebleven was",
        body: "Valt je verbinding weg, dan gaat hij vanzelf verder. Voeg de map opnieuw toe en alleen wat ontbreekt wordt verstuurd.",
        alt: "Een melding in de uploader dat een map niet helemaal is geüpload, met een knop “Resume”.",
      },
      {
        id: "phone",
        title: "Ook overzichtelijk op je telefoon",
        body: "Op een klein scherm klapt het mappenpad in zodat het past, en als je op “…” tikt zie je de mappen ertussen. Zo weet je altijd waar je foto's naartoe gaan.",
        alt: "De uploader op een telefoon: het mappenpad toont de huidige map, en “…” opent een lijst van de mappen daarboven.",
      },
    ],
  },
  ftp: {
    date: "1 november 2026",
    heading: "Wat er op 1 november verandert aan FTP",
    body: "Onze altijd actieve gedeelde FTP-servers sluiten op 1 november 2026. Gebruik je nu FTP? Stap dan vóór die datum over.",
    points: [
      {
        id: "closes",
        title: "Gedeelde FTP sluit op 1 november 2026",
        body: "De altijd actieve gedeelde FTP-servers gaan op 1 november 2026 uit.",
      },
      {
        id: "standard",
        title: "De web-uploader is de standaardmanier om te uploaden",
        body: "Hij zit in alle abonnementen, en je kunt hem vandaag al gebruiken.",
      },
      {
        id: "ondemand",
        title: "Als FTP echt nodig is",
        body: "Bij alle abonnementen kan iedereen in je werkruimte vanuit de web-uploader een eigen FTP-server op aanvraag starten, op een persoonlijk adres dat nooit verandert. Na een uur zonder activiteit gaat hij vanzelf uit.",
      },
    ],
    helpLink: "Zo werkt FTP op aanvraag",
  },
  more: {
    previewFirstTitle: "Zo werken uploads met eerst de previews",
    previewFirstBody: "Bij alle abonnementen. Stap voor stap: de previews, online gaan en wat kopers zien.",
  },
  cta: {
    heading: "Probeer het bij je volgende upload",
    body: "De nieuwe web-uploader staat klaar bij alle abonnementen. Voeg een map toe en zie je galerij online gaan.",
    button: "Open de web-uploader",
  },
  pause: "Animatie pauzeren",
  play: "Animatie afspelen",
};

export default nl;
